/**
 * Diff a parser ParseResult against the live workspace store and apply the
 * difference as a sequence of optimistic store actions. Used by the
 * editable PlantUML pane (M2.6) so a text edit becomes a structural change
 * in the canvas.
 *
 * Matching rules:
 * - **Concepts** are matched by alias. If the alias is `n_<uuid>` (the form
 *   the emitter produces), reverse-sanitise to recover the existing
 *   concept's id. If the alias is anything else, treat as a fresh concept
 *   the user just typed → mint a new UUID and create it.
 * - **Relations** have no alias of their own in the textual form, so we
 *   match by `(sourceConceptId, targetConceptId, typeId)`. If that exact
 *   tuple already exists, we update its label; otherwise we create.
 *   Relations whose tuple no longer appears in the text are removed.
 *
 * "Removed" betekent: van het ACTIEVE DIAGRAM gehaald, niet uit het model —
 * net als Delete op het canvas. Een regel weghalen (of tijdens het typen
 * even kapotmaken) mag nooit stil een object uit de repository gooien dat
 * mogelijk op andere diagrammen staat. Uit het model verwijderen doe je
 * bewust via Cmd/Ctrl/Alt+Delete (met bevestiging). Zonder `scope` is het
 * pad puur additief: niets wordt weggehaald.
 *
 * The function returns a small summary so the caller can decide whether to
 * surface a "synced N changes" toast.
 */
import type { ParseResult, Relation } from "@ae/domain";
import { aliasToConceptId, procedureForConcept } from "@ae/domain";
import { ApiError, api } from "../../api/client";
import type { WorkspaceStoreState } from "../../state/workspace";
import { diagramEntryKeysForRows } from "../grid-pane/diagramRemoval";

export interface ApplyDiff {
  conceptsCreated: number;
  conceptsUpdated: number;
  conceptsRemoved: number;
  relationsCreated: number;
  relationsUpdated: number;
  relationsRemoved: number;
}

/**
 * Optional scope-restriction. When the caller is showing a per-diagram
 * subset of the workspace's concepts/relations in the editor, only
 * those ids should be considered for the "in store but missing from
 * text → delete" pass. Without this, switching to a smaller subset and
 * letting the parser run would tombstone every concept that ISN'T on
 * the current diagram. Out-of-scope concepts are left untouched.
 *
 * `null` / `undefined` = additief: er wordt niets weggehaald. (Vroeger
 * betekende dit "elk workspace-concept dat niet in de tekst staat wordt
 * verwijderd" — de PlantUML-import in de tabel riep zo zonder scope aan en
 * gooide daarmee alles buiten het geplakte fragment uit het model.)
 */
export interface ApplyScope {
  conceptIds: Set<string>;
  relationIds: Set<string>;
}

export async function applyParseResult(
  state: WorkspaceStoreState,
  workspaceId: string,
  branchId: string,
  result: ParseResult,
  lang: "nl" | "en",
  scope?: ApplyScope | null,
): Promise<ApplyDiff> {
  const diff: ApplyDiff = {
    conceptsCreated: 0,
    conceptsUpdated: 0,
    conceptsRemoved: 0,
    relationsCreated: 0,
    relationsUpdated: 0,
    relationsRemoved: 0,
  };

  // ---- Concepts ----------------------------------------------------------
  const store = state.store;
  const existingConcepts = store.listConcepts().filter((c) => !c.tombstoneInBranch);
  const seenConceptIds = new Set<string>();
  // alias-as-typed → resolved concept id (existing or freshly minted).
  const aliasToId = new Map<string, string>();

  // Stagger creates so each new concept lands on its own grid slot.
  let nextNewSlot = 0;
  // Per-parent counter for relative-position layout when nesting children
  // into a Grouping. Each child gets its own slot inside the parent.
  const nestedSlotByParent = new Map<string, number>();

  for (const parsed of result.concepts) {
    const existingId = aliasToConceptId(parsed.alias);
    const existing = existingId ? store.getConcept(existingId) : null;
    if (existing && !existing.tombstoneInBranch) {
      aliasToId.set(parsed.alias, existing.id);
      seenConceptIds.add(existing.id);
      // Diff label and type. Trim aan beide kanten zodat de Junction-
      // empty-label-workaround (emitter vervangt "" door " " voor
      // Junction_And/Or — anders breekt kroki) niet als label-change
      // wordt geïnterpreteerd. Een gebruiker die expliciet whitespace
      // typt valt onder dezelfde normalisatie — acceptabel trade-off.
      const currentLabel = (
        existing.name[lang] ??
        existing.name.nl ??
        existing.name.en ??
        ""
      ).trim();
      const labelChanged = currentLabel !== parsed.label.trim();
      // Type-wissel alleen honoreren als 't een ECHTE wissel is. Sommige
      // types emitten naar dezelfde PlantUML-procedure als een ander type
      // (bv. `sharp.Attribute` / `sharp.Identifier` → `Business_Object`,
      // net als `archimate.BusinessObject`). De parser leest zo'n regel
      // altijd terug als het "hoofd"-type, waardoor een re-apply het rijkere
      // bestaande type zou wegschrijven (sharp.Attribute → BusinessObject).
      // Wanneer beide types dezelfde procedure delen, is de "wissel" puur die
      // lossy emitter-fallback → behoud het bestaande type.
      // Sinds de ArchiMate-4-merge geldt dit óók voor elk gemigreerd concept:
      // een `archimate.Process` wordt als `Business_Process` geëmit (PlantUML
      // kent geen generiek Process) en komt terug als
      // `archimate.BusinessProcess`. Beide kanten via `procedureForConcept`
      // resolven maakt daar een no-op van — zonder dat zou élke tekst-edit het
      // concept naar een gedeactiveerd 3.x-type hertyperen. Het profiel telt
      // mee, zodat een Process met «Application Process» tegen
      // `Application_Process` wordt gelegd en niet tegen de born-v4-default.
      const existingProc = procedureForConcept(existing.typeId, existing.stereotypes);
      const sameProcedure =
        existingProc !== undefined && existingProc === procedureForConcept(parsed.typeId);
      const typeChanged = existing.typeId !== parsed.typeId && !sameProcedure;
      if (labelChanged || typeChanged) {
        const patch: { name?: Record<string, string>; type_id?: string } = {};
        if (labelChanged) patch.name = { ...existing.name, [lang]: parsed.label.trim() };
        if (typeChanged) patch.type_id = parsed.typeId;
        try {
          await state.patchConcept(workspaceId, branchId, existing.id, patch);
          diff.conceptsUpdated++;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`patchConcept(${existing.id}) failed`, err);
        }
      }
      // Auto-translate the OTHER language so editing NL keeps EN in sync
      // (and vice versa). Mirrors BilingualField's last-edit-wins blur
      // behaviour. Fire-and-forget; the patch lands when Claude responds.
      // Skip wanneer caller (PlantUML-paste) heeft aangegeven het
      // achterwege te laten — die delegeert vertaling naar de bulk
      // "Werk vertalingen bij" met swap-detect.
      if (labelChanged) {
        scheduleTranslation(
          state,
          workspaceId,
          branchId,
          "concept",
          existing.id,
          parsed.label,
          lang,
        );
      }
      // Re-apply visual nesting on the existing concept too, so a paste
      // with brace-blocks that names already-existing concepts pulls them
      // back into the right Grouping.
      maybeNestUnderContainer(state, parsed, existing.id, aliasToId, nestedSlotByParent);
      continue;
    }
    // Fresh concept — create with this language slot filled. Wrapped in
    // try/catch so a single rejection (matrix violation, network blip,
    // server-side validation error) doesn't abort the whole paste — the
    // remaining concepts + relations still get a chance to land.
    try {
      const created = await state.addConceptOnCanvas(
        workspaceId,
        branchId,
        parsed.typeId,
        { [lang]: parsed.label },
        gridSlot(nextNewSlot++),
      );
      aliasToId.set(parsed.alias, created.id);
      seenConceptIds.add(created.id);
      diff.conceptsCreated++;
      if (parsed.label) {
        scheduleTranslation(
          state,
          workspaceId,
          branchId,
          "concept",
          created.id,
          parsed.label,
          lang,
        );
      }
      // Visual nesting from `Grouping_Group(g, "…") { … }` blocks: tag
      // the child's view-state with `containerId = parent` and a small
      // relative position so React Flow renders it inside the Grouping.
      // Same data-shape as the manual drag-drop "inschuiven" gesture.
      maybeNestUnderContainer(state, parsed, created.id, aliasToId, nestedSlotByParent);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`addConceptOnCanvas(${parsed.typeId}, "${parsed.label}") failed`, err);
    }
  }

  // Concepts in the store but not referenced in the text → van het
  // diagram halen (aan het eind, samen met de relaties). Alleen binnen de
  // scope (= wat de tekst toont); zonder scope wordt niets weggehaald.
  const conceptsToHide: string[] = [];
  for (const c of existingConcepts) {
    if (seenConceptIds.has(c.id)) continue;
    if (!scope?.conceptIds.has(c.id)) continue;
    conceptsToHide.push(c.id);
  }

  // ---- Relations ---------------------------------------------------------
  // Resolving relations needs the alias map populated with both old + new
  // concept ids. After concept-phase above, that's the case.
  const resolveAlias = (alias: string): string | null => {
    const fromMap = aliasToId.get(alias);
    if (fromMap) return fromMap;
    // It's possible the user references a concept by its `n_<uuid>` alias
    // without typing the concept itself — only valid if the concept already
    // exists in the store.
    const existingId = aliasToConceptId(alias);
    if (existingId && store.getConcept(existingId)) {
      return existingId;
    }
    return null;
  };

  // Build a lookup of existing relations keyed by (source, target, typeId)
  // so we can match parsed relations against them.
  const existingRelations = store.listRelations().filter((r) => !r.tombstoneInBranch);
  const relationKey = (src: string, tgt: string, typeId: string) => `${src} ${tgt} ${typeId}`;
  const existingByKey = new Map<string, Relation>();
  for (const r of existingRelations) {
    existingByKey.set(relationKey(r.sourceConceptId, r.targetConceptId, r.typeId), r);
  }
  const seenRelationIds = new Set<string>();

  for (const parsed of result.relations) {
    const sourceId = resolveAlias(parsed.sourceAlias);
    const targetId = resolveAlias(parsed.targetAlias);
    if (!sourceId || !targetId) {
      // Unresolved alias — skip silently. The parser already emits errors
      // for genuine syntax problems; this case usually means the user is
      // mid-edit and we'll catch up on the next debounce. Also covers the
      // case where a referenced concept's create-step failed earlier in
      // this same apply (its alias never made it into aliasToId).
      continue;
    }
    const key = relationKey(sourceId, targetId, parsed.typeId);
    const match = existingByKey.get(key);
    if (match) {
      seenRelationIds.add(match.id);
      // Per-diagram-scoping: zorg dat een gematchte relatie ook
      // zichtbaar is op het ACTIEVE diagram als de gebruiker 'm hier
      // intypt. `placeExistingRelationOnDiagram` zet alleen de
      // visibility-marker als die nog ontbreekt — bestaande markers
      // blijven onaangeroerd (idempotent).
      state.placeExistingRelationOnDiagram(workspaceId, branchId, match.id);
      const currentLabel = match.label[lang] ?? match.label.nl ?? match.label.en ?? "";
      if (currentLabel !== parsed.label) {
        try {
          await state.patchRelation(workspaceId, branchId, match.id, {
            label: { ...match.label, [lang]: parsed.label },
          });
          diff.relationsUpdated++;
          if (parsed.label) {
            scheduleTranslation(
              state,
              workspaceId,
              branchId,
              "relation",
              match.id,
              parsed.label,
              lang,
            );
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`patchRelation(${match.id}) failed`, err);
        }
      }
      maybeUpdateHandles(state, workspaceId, branchId, match.id, parsed);
      continue;
    }
    // New relation. Same per-item resilience as the concept loop above:
    // a matrix-validator rejection on one synthetic Aggregation must not
    // abort the rest of the paste.
    try {
      const created = await state.connectConcepts(
        workspaceId,
        branchId,
        sourceId,
        targetId,
        parsed.typeId,
      );
      seenRelationIds.add(created.id);
      diff.relationsCreated++;
      if (parsed.label) {
        try {
          await state.patchRelation(workspaceId, branchId, created.id, {
            label: { [lang]: parsed.label },
          });
          scheduleTranslation(
            state,
            workspaceId,
            branchId,
            "relation",
            created.id,
            parsed.label,
            lang,
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`patchRelation(label, ${created.id}) failed`, err);
        }
      }
      maybeUpdateHandles(state, workspaceId, branchId, created.id, parsed);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        `connectConcepts(${parsed.typeId}, ${parsed.sourceAlias}→${parsed.targetAlias}) failed`,
        err,
      );
    }
  }

  // Relations no longer referenced → van het diagram halen. Same
  // scope-restriction logic as concepts above.
  const relationsToHide: string[] = [];
  for (const r of existingRelations) {
    if (seenRelationIds.has(r.id)) continue;
    if (!scope?.relationIds.has(r.id)) continue;
    relationsToHide.push(r.id);
  }
  if (conceptsToHide.length > 0 || relationsToHide.length > 0) {
    const hidden = hideFromActiveDiagram(
      state,
      workspaceId,
      branchId,
      conceptsToHide,
      relationsToHide,
    );
    diff.conceptsRemoved += hidden.concepts;
    diff.relationsRemoved += hidden.relations;
  }

  return diff;
}

/**
 * Haal concepten + relaties van het actieve diagram (alle instances), als
 * één undo-stap. Eerst de relaties, dan de concepten: hideConceptOnDiagram
 * neemt afhankelijke relatie-instances zelf mee. Een relatie zonder eigen
 * entry (legacy, overal zichtbaar) krijgt via hideRelationOnDiagram een
 * `hidden`-marker — behalve als een endpoint toch al verdwijnt.
 */
function hideFromActiveDiagram(
  state: WorkspaceStoreState,
  workspaceId: string,
  branchId: string,
  conceptIds: string[],
  relationIds: string[],
): { concepts: number; relations: number } {
  const diagramId = state.activeDiagramId;
  const diagram = diagramId ? state.store.getDiagram(diagramId) : undefined;
  if (!diagram) return { concepts: 0, relations: 0 };
  const { conceptKeys, relationKeys } = diagramEntryKeysForRows(
    diagram.viewState,
    conceptIds,
    relationIds,
  );
  const hiddenConcepts = new Set(conceptIds);
  const relationsWithEntry = new Set(relationKeys.map((k) => diagram.viewState[k]?.ref ?? k));
  const legacyRelations = relationIds.filter((id) => {
    if (relationsWithEntry.has(id)) return false;
    const rel = state.store.getRelation(id);
    if (!rel) return false;
    return !hiddenConcepts.has(rel.sourceConceptId) && !hiddenConcepts.has(rel.targetConceptId);
  });
  // Tellen vóór het verbergen — daarna zijn de entries weg.
  const conceptCount = new Set(conceptKeys.map((k) => diagram.viewState[k]?.ref ?? k)).size;
  const relationCount = relationsWithEntry.size + legacyRelations.length;
  state.beginUserUndoBatch();
  try {
    for (const key of relationKeys) state.hideRelationOnDiagram(workspaceId, branchId, key);
    for (const id of legacyRelations) state.hideRelationOnDiagram(workspaceId, branchId, id);
    for (const key of conceptKeys) state.hideConceptOnDiagram(workspaceId, branchId, key);
  } finally {
    state.endUserUndoBatch("Verwijder van diagram (Onderwater venster)");
  }
  return { concepts: conceptCount, relations: relationCount };
}

/**
 * Coalesce repeated translate calls per (kind, id) so rapid keystrokes in
 * the text pane only trigger one outbound translation. Each entry is a
 * `setTimeout` that fires after a short idle window, performs the
 * translation, and patches the OTHER language slot.
 *
 * Last-edit-wins matches the BilingualField blur behaviour: we always
 * overwrite the target language with Claude's translation of the
 * just-edited source label.
 */
const TRANSLATE_DEBOUNCE_MS = 600;
const pendingTranslations = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleTranslation(
  state: WorkspaceStoreState,
  workspaceId: string,
  branchId: string,
  kind: "concept" | "relation",
  id: string,
  sourceText: string,
  sourceLang: "nl" | "en",
): void {
  if (!sourceText.trim()) return;
  const key = `${kind}:${id}`;
  const existing = pendingTranslations.get(key);
  if (existing) clearTimeout(existing);
  const targetLang: "nl" | "en" = sourceLang === "nl" ? "en" : "nl";
  const timer = setTimeout(async () => {
    pendingTranslations.delete(key);
    try {
      const res = await api.translate({
        source_lang: sourceLang,
        target_lang: targetLang,
        text: sourceText,
        kind: "name",
      });
      // Swap-detect: wanneer Claude exact dezelfde tekst teruggeeft
      // (case-insensitive trim-match) was de bron-taal-aanname mis —
      // de tekst was eigenlijk al in target-taal. Doe omgekeerde
      // translate om de echte bron-taal-versie te krijgen, en swap de
      // velden zodat tekst in juiste-taal-veld landt. Lost taal-
      // mismatch-paste-flow op (bv. EN-PlantUML bij NL-UI).
      // Caveat: eigennamen/korte tokens (Apple, CRM) zijn legitiem
      // identiek → false-positive, één verspilde Claude-call, geen
      // schade.
      const identical = res.text.trim().toLowerCase() === sourceText.trim().toLowerCase();
      let finalSourceText = sourceText;
      let finalTargetText = res.text;
      if (identical) {
        try {
          const swap = await api.translate({
            source_lang: targetLang, // omgekeerd
            target_lang: sourceLang,
            text: sourceText,
            kind: "name",
          });
          // Tekst was eigenlijk target-taal; swap-result is de juiste
          // bron-taal-versie. Tekst-velden swappen zodat record klopt.
          finalSourceText = swap.text;
          finalTargetText = sourceText;
        } catch {
          // Swap-call faalde — val terug op de identieke eerste-translate.
        }
      }
      // Re-read the latest record so we don't clobber concurrent edits to
      // unrelated fields.
      if (kind === "concept") {
        const cur = state.store.getConcept(id);
        if (!cur) return;
        await state.patchConcept(workspaceId, branchId, id, {
          name: {
            ...cur.name,
            [sourceLang]: finalSourceText,
            [targetLang]: finalTargetText,
          },
        });
      } else {
        const cur = state.store.getRelation(id);
        if (!cur) return;
        await state.patchRelation(workspaceId, branchId, id, {
          label: {
            ...cur.label,
            [sourceLang]: finalSourceText,
            [targetLang]: finalTargetText,
          },
        });
      }
    } catch (err) {
      // 412 = no qa_live_ key configured — silent. Other errors surface
      // through the workspace store's lastError on the next user-visible
      // action, but we don't want to block text-pane edits on translation.
      if (err instanceof ApiError && err.status === 412) return;
      // Swallow — the user can re-trigger by editing again.
    }
  }, TRANSLATE_DEBOUNCE_MS);
  pendingTranslations.set(key, timer);
}

/**
 * Decide which handle pair to write into view_state for a parsed relation:
 * - Explicit `@handles` directive → use that.
 * - No directive, no existing handles → derive defaults from the relative
 *   box positions so the next emit produces a `@handles …` line. (A user
 *   who types a brand-new relation in the text pane otherwise wouldn't see
 *   the directive appear, which contradicts the rest of the canvas.)
 * - No directive, existing handles → leave them alone.
 */
function maybeUpdateHandles(
  state: WorkspaceStoreState,
  workspaceId: string,
  branchId: string,
  relationId: string,
  parsed: { sourceHandle?: string | undefined; targetHandle?: string | undefined },
): void {
  const diagramId = state.activeDiagramId;
  if (!diagramId) return;
  const diagram = state.store.getDiagram(diagramId);
  const ve = diagram?.viewState[relationId];
  const currentSource = ve?.sourceHandle ?? null;
  const currentTarget = ve?.targetHandle ?? null;

  let nextSource = parsed.sourceHandle ?? currentSource;
  let nextTarget = parsed.targetHandle ?? currentTarget;

  // Fill in defaults from box positions when neither side is set.
  if (!nextSource && !nextTarget) {
    const relation = state.store.getRelation(relationId);
    if (relation && diagram) {
      const sourcePos = diagram.viewState[relation.sourceConceptId]?.position;
      const targetPos = diagram.viewState[relation.targetConceptId]?.position;
      if (sourcePos && targetPos) {
        const pair = pickDefaultHandles(sourcePos, targetPos);
        nextSource = pair.source;
        nextTarget = pair.target;
      }
    }
  }

  if (nextSource === currentSource && nextTarget === currentTarget) return;
  state.setRelationHandlesOnCanvas(workspaceId, branchId, relationId, nextSource, nextTarget);
}

/**
 * Pick the most natural handle pair given the source and target node
 * positions: dominant axis (horizontal vs vertical) decides which sides
 * are touched, and the sign of the delta decides which side on each box.
 */
function pickDefaultHandles(
  sourcePos: { x: number; y: number },
  targetPos: { x: number; y: number },
): { source: string; target: string } {
  const dx = targetPos.x - sourcePos.x;
  const dy = targetPos.y - sourcePos.y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? { source: "right", target: "left" } : { source: "left", target: "right" };
  }
  return dy >= 0 ? { source: "bottom", target: "top" } : { source: "top", target: "bottom" };
}

function gridSlot(i: number): { x: number; y: number } {
  // Place new-from-text concepts on a small grid in the bottom-right of
  // the canvas. Auto-layout is M2.7+; this just keeps them visible.
  const cols = 4;
  const col = i % cols;
  const row = Math.floor(i / cols);
  return { x: 600 + col * 220, y: 400 + row * 140 };
}

/**
 * Apply visual nesting from a parsed brace-block: tag the child's
 * view-state with `containerId = parent.instanceId` and a small
 * relative position so React Flow renders it inside the Grouping.
 *
 * Sized large enough that several children fit side-by-side without
 * the user immediately needing to resize the parent. We also nudge
 * the parent's width/height up so the children don't overflow on
 * first render (the user can still resize manually).
 *
 * Idempotent: re-applying with the same parent leaves an already-
 * correctly-nested child alone.
 */
function maybeNestUnderContainer(
  state: WorkspaceStoreState,
  parsed: { containerAlias?: string },
  childConceptId: string,
  aliasToId: Map<string, string>,
  nestedSlotByParent: Map<string, number>,
): void {
  if (!parsed.containerAlias) return;
  const diagramId = state.activeDiagramId;
  if (!diagramId) return;
  const diagram = state.store.getDiagram(diagramId);
  if (!diagram) return;
  const parentConceptId = aliasToId.get(parsed.containerAlias);
  if (!parentConceptId) return;
  // Use the canonical instance (key === conceptId) as the parent. Multi-
  // instance handling for paste-time nesting isn't required for now —
  // brace-block syntax doesn't disambiguate between multiple visual
  // instances of the same concept.
  const parentVe = diagram.viewState[parentConceptId];
  if (!parentVe || parentVe.position === undefined) return;
  const childVe = diagram.viewState[childConceptId];
  if (!childVe) return;

  // Already nested under the right parent? Don't re-shuffle.
  if (childVe.containerId === parentConceptId) return;

  // Per-parent grid: 2 cols, room for typical-width children.
  const slot = nestedSlotByParent.get(parentConceptId) ?? 0;
  nestedSlotByParent.set(parentConceptId, slot + 1);
  const cols = 2;
  const colW = 220;
  const rowH = 80;
  const padX = 20;
  const padY = 60; // leave room for the grouping's own title-bar.
  const col = slot % cols;
  const row = Math.floor(slot / cols);
  const relPos = { x: padX + col * colW, y: padY + row * rowH };

  state.store.setViewElementState(diagramId, childConceptId, {
    ...childVe,
    containerId: parentConceptId,
    position: relPos,
  });

  // Grow the parent so the children fit inside on first render. The
  // user can shrink afterwards via the resize handle.
  const rowsNeeded = Math.floor(slot / cols) + 1;
  const minWidth = padX * 2 + cols * colW;
  const minHeight = padY + rowsNeeded * rowH + padY / 2;
  const nextWidth = Math.max(parentVe.width ?? 0, minWidth);
  const nextHeight = Math.max(parentVe.height ?? 0, minHeight);
  if (nextWidth !== parentVe.width || nextHeight !== parentVe.height) {
    state.store.setViewElementState(diagramId, parentConceptId, {
      ...parentVe,
      width: nextWidth,
      height: nextHeight,
    });
  }
}
